import "./styles.css";
import { formatBytes, formatCount, getSeries, getSeriesDetail, getStats, login, logout, type Episode, type SeriesSummary } from "./api.js";
import { createPlaybackSession } from "./playback-session.js";
import { variantOptions, type VideoVariant } from "./playback-policy.js";
import { attachPlayer, type PlayerAudioTrack } from "./player.js";
import { browserSubtitleTrackHost, replaceSubtitleTracks, selectSubtitle } from "./subtitles.js";

const app = document.querySelector<HTMLElement>("#app") ?? missingAppRoot();

if (window.location.pathname === "/login") renderLogin();
else {
  const seriesId = new URLSearchParams(window.location.search).get("series");
  if (seriesId) void renderSeries(seriesId);
  else void renderDashboard();
}

function renderLogin(): void {
  app.innerHTML = `<section class="login-page"><div class="login-card"><div class="brand-mark">GD</div><p class="eyebrow">PRIVATE LIBRARY</p><h1>GoodDrama Preview</h1><p class="muted">Đăng nhập để xem catalog và video riêng tư trên R2.</p><form id="login-form"><label for="password">Mật khẩu</label><input id="password" name="password" type="password" autocomplete="current-password" required autofocus><button type="submit">Mở thư viện</button><p id="login-error" class="error" role="alert"></p></form></div></section>`;
  const form = document.querySelector<HTMLFormElement>("#login-form")!;
  const error = document.querySelector<HTMLElement>("#login-error")!;
  form.addEventListener("submit", event => {
    event.preventDefault();
    const button = form.querySelector<HTMLButtonElement>("button")!;
    const password = new FormData(form).get("password");
    if (typeof password !== "string") return;
    button.disabled = true;
    button.textContent = "Đang đăng nhập…";
    error.textContent = "";
    void login(password).then(() => window.location.assign("/")).catch(reason => {
      error.textContent = reason instanceof Error ? reason.message : "Không thể đăng nhập";
      button.disabled = false;
      button.textContent = "Mở thư viện";
    });
  });
}

async function renderDashboard(): Promise<void> {
  app.innerHTML = shell(`<section class="hero"><div><p class="eyebrow">R2 VIDEO LIBRARY</p><h1>Kho phim của bạn</h1><p class="muted">Toàn bộ series đã crawl và xác minh hoàn tất.</p></div><form id="search-form" class="search"><input id="search" type="search" placeholder="Tìm tên phim hoặc series ID…" aria-label="Tìm kiếm"><button type="submit">Tìm</button></form></section><section id="stats" class="stats skeleton"></section><div class="section-head"><h2>Series hoàn tất</h2><span id="series-count" class="pill">Đang tải</span></div><section id="series-grid" class="series-grid"><p class="muted">Đang tải catalog…</p></section><nav id="pagination" class="pagination" aria-label="Phân trang"></nav>`);
  bindLogout();
  let page = 1;
  let query = "";
  const load = async (): Promise<void> => {
    const grid = document.querySelector<HTMLElement>("#series-grid")!;
    grid.innerHTML = `<p class="muted">Đang tải catalog…</p>`;
    try {
      const result = await getSeries(page, query);
      document.querySelector<HTMLElement>("#series-count")!.textContent = `${formatCount(result.pagination.total)} bộ`;
      grid.replaceChildren(...result.data.map(seriesCard));
      if (result.data.length === 0) grid.innerHTML = `<p class="empty">Không tìm thấy series phù hợp.</p>`;
      renderPagination(result.pagination.page, result.pagination.totalPages, nextPage => { page = nextPage; void load(); window.scrollTo({ top: 0, behavior: "smooth" }); });
    } catch (error) {
      grid.innerHTML = `<p class="error">${error instanceof Error ? error.message : "Không thể tải catalog"}</p>`;
    }
  };
  document.querySelector<HTMLFormElement>("#search-form")!.addEventListener("submit", event => {
    event.preventDefault();
    query = document.querySelector<HTMLInputElement>("#search")!.value.trim();
    page = 1;
    void load();
  });
  await Promise.all([load(), loadStats()]);
}

async function loadStats(): Promise<void> {
  const container = document.querySelector<HTMLElement>("#stats")!;
  try {
    const { data, publishedAt } = await getStats();
    const cards = [
      ["Series", formatCount(data.completedSeries)],
      ["Tập hoàn tất", formatCount(data.verified)],
      ["Assets", formatCount(data.assets)],
      ["Đã lưu", formatBytes(data.bytes)],
      ["Đang tải", formatCount(data.discovering)],
      ["Blocked", formatCount(data.blocked)],
    ];
    container.classList.remove("skeleton");
    container.innerHTML = cards.map(([label, value]) => `<article><strong>${value}</strong><span>${label}</span></article>`).join("");
    container.title = `Catalog cập nhật ${new Date(publishedAt).toLocaleString("vi-VN")}`;
  } catch (error) {
    container.innerHTML = `<p class="error">${error instanceof Error ? error.message : "Không thể tải thống kê"}</p>`;
  }
}

function seriesCard(series: SeriesSummary): HTMLElement {
  const link = document.createElement("a");
  link.className = "series-card";
  link.href = `/?series=${encodeURIComponent(series.seriesId)}`;
  const image = document.createElement("img");
  image.src = `/media/${series.posterKey}`;
  image.alt = `Poster ${series.title}`;
  image.loading = "lazy";
  const content = document.createElement("div");
  const title = document.createElement("h3");
  title.textContent = series.title;
  const meta = document.createElement("p");
  meta.textContent = `${series.verifiedEpisodes}/${series.totalEpisodes} tập`;
  content.append(title, meta);
  link.append(image, content);
  return link;
}

async function renderSeries(seriesId: string): Promise<void> {
  app.innerHTML = shell(`<a class="back" href="/">← Quay lại thư viện</a><section id="series-detail" class="detail"><p class="muted">Đang tải series…</p></section>`);
  bindLogout();
  const container = document.querySelector<HTMLElement>("#series-detail")!;
  try {
    const series = await getSeriesDetail(seriesId);
    container.innerHTML = `<div class="detail-copy"><img class="detail-poster" alt=""><div><p class="eyebrow">${series.verifiedEpisodes} TẬP HOÀN TẤT</p><h1></h1><p class="description"></p></div></div><section class="player-panel"><div class="video-wrap"><video id="video" controls playsinline preload="metadata"></video><p id="player-error" class="player-error"></p></div><div class="player-controls"><label>Tập<select id="episode"></select></label><label>Luồng<select id="variant"></select></label><label id="audio-control" hidden>Âm thanh<select id="audio"></select></label><label>Phụ đề<select id="subtitle"><option value="">Tắt phụ đề</option></select></label></div></section>`;
    const poster = container.querySelector<HTMLImageElement>(".detail-poster")!;
    poster.src = `/media/${series.posterKey}`;
    poster.alt = `Poster ${series.title}`;
    container.querySelector("h1")!.textContent = series.title;
    container.querySelector<HTMLElement>(".description")!.textContent = series.description ?? "Chưa có mô tả.";
    const episodeSelect = container.querySelector<HTMLSelectElement>("#episode")!;
    for (const episode of series.episodes) episodeSelect.add(new Option(`Tập ${episode.episodeNumber}`, episode.episodeId));
    const variantSelect = container.querySelector<HTMLSelectElement>("#variant")!;
    for (const option of variantOptions()) variantSelect.add(new Option(option.label, option.value));
    const audioControl = container.querySelector<HTMLLabelElement>("#audio-control")!;
    const audioSelect = container.querySelector<HTMLSelectElement>("#audio")!;
    const subtitleSelect = container.querySelector<HTMLSelectElement>("#subtitle")!;
    const video = container.querySelector<HTMLVideoElement>("#video")!;
    const subtitleVideo = browserSubtitleTrackHost(video);
    const playerError = container.querySelector<HTMLElement>("#player-error")!;
    const populateAudioTracks = (tracks: PlayerAudioTrack[], selectedId: string | null): void => {
      audioSelect.replaceChildren();
      for (const track of tracks) {
        const label = track.locale && track.locale !== track.label ? `${track.label} (${track.locale})` : track.label;
        audioSelect.add(new Option(label, track.id));
      }
      audioControl.hidden = tracks.length === 0;
      audioSelect.disabled = tracks.length === 0;
      if (selectedId && tracks.some(track => track.id === selectedId)) audioSelect.value = selectedId;
    };
    const player = createPlaybackSession(video, attachPlayer, {
      onVariant: variant => {
        variantSelect.value = variant;
        playerError.textContent = "";
      },
      onAudioTracks: populateAudioTracks,
      onTerminalError: message => { playerError.textContent = message; },
    });
    const populateSubtitles = (episode: Episode, selected: string): void => {
      subtitleSelect.replaceChildren(new Option("Tắt phụ đề", ""));
      for (const track of episode.subtitles) subtitleSelect.add(new Option(track.displayName, track.language));
      subtitleSelect.value = selected;
    };
    const play = (preserveSelection: boolean): void => {
      replaceSubtitleTracks(subtitleVideo, [], "");
      playerError.textContent = "";
      const episode = series.episodes.find(item => item.episodeId === episodeSelect.value) ?? series.episodes[0];
      if (!episode) { playerError.textContent = "Series chưa có tập phát được."; return; }
      const selected = selectSubtitle(episode.subtitles, subtitleSelect.value, !preserveSelection);
      populateSubtitles(episode, selected);
      player.loadEpisode({
        main: `/media/${episode.variants.main}`,
        fallback: `/media/${episode.variants.fallback}`,
      });
      replaceSubtitleTracks(subtitleVideo, episode.subtitles, selected);
    };
    episodeSelect.addEventListener("change", () => play(false));
    variantSelect.addEventListener("change", () => {
      const variant: VideoVariant = variantSelect.value === "main" ? "main" : "fallback";
      player.selectVariant(variant);
    });
    audioSelect.addEventListener("change", () => player.selectAudioTrack(audioSelect.value));
    subtitleSelect.addEventListener("change", () => {
      const episode = series.episodes.find(item => item.episodeId === episodeSelect.value);
      if (episode) replaceSubtitleTracks(subtitleVideo, episode.subtitles, selectSubtitle(episode.subtitles, subtitleSelect.value, false));
    });
    play(false);
    window.addEventListener("pagehide", () => { player.destroy(); replaceSubtitleTracks(subtitleVideo, [], ""); }, { once: true });
  } catch (error) {
    container.innerHTML = `<p class="error">${error instanceof Error ? error.message : "Không thể tải series"}</p>`;
  }
}

function shell(content: string): string {
  return `<header class="topbar"><a class="brand" href="/"><span>GD</span> GoodDrama</a><button id="logout" class="ghost">Đăng xuất</button></header><div class="container">${content}</div>`;
}

function bindLogout(): void {
  document.querySelector<HTMLButtonElement>("#logout")?.addEventListener("click", () => {
    void logout().finally(() => window.location.assign("/login"));
  });
}

function renderPagination(current: number, total: number, onPage: (page: number) => void): void {
  const nav = document.querySelector<HTMLElement>("#pagination")!;
  nav.replaceChildren();
  if (total <= 1) return;
  const previous = document.createElement("button");
  previous.textContent = "← Trước";
  previous.disabled = current <= 1;
  previous.addEventListener("click", () => onPage(current - 1));
  const label = document.createElement("span");
  label.textContent = `Trang ${current} / ${total}`;
  const next = document.createElement("button");
  next.textContent = "Sau →";
  next.disabled = current >= total;
  next.addEventListener("click", () => onPage(current + 1));
  nav.append(previous, label, next);
}

function missingAppRoot(): never {
  throw new Error("Missing app root");
}
